/// <amd-module name="TFS/Admin/Views/ProjectPermissions/Sources/ProjectPermissionsAPIWrapper" />
import { IVssComponentContext } from "VSS/Platform/Layout";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IIdentity } from "VSSUI/IdentityPicker";
import { ProjectPermissionsSource } from "Sources/ProjectPermissonsSource";
import { GraphUserOriginIdCreationContext } from "VSS/Platform/Contracts/Graph";
import { PermissionLevelAssignment } from "TFS/Admin/Common/Contracts/PermissionLevel";

export class ProjectPermissionsAPIWrapper {
    private context: IVssComponentContext;
    private telemetryLogger: AdminTelemetry;

    private _restClientSource: ProjectPermissionsSource;

    constructor(context: IVssComponentContext, telemetryLogger?: AdminTelemetry) {
        this.context = context;
        if (!this.telemetryLogger) this.telemetryLogger = new AdminTelemetry(this.context.pageContext);
        this._restClientSource = new ProjectPermissionsSource();
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.ProjectPermissions, Feature.ProjectPermissions, action, properties);
    };

    public assignPermission(selectedUser: IIdentity, definitionId: string, resourceId: string): Promise<PermissionLevelAssignment | null> {
        var actionTelemetryLogger = this._startActionCapture("Add Member - Assign Permission Level", {
            SubjectDescriptor: selectedUser.subjectDescriptor
        });

        //Read the current assignments for the subject for te project
        return new Promise(async (resolve, reject) => {
            if (!selectedUser.subjectDescriptor) {
                actionTelemetryLogger.captureError("subject descriptor does not exist.");
                return resolve(null);
            }
            await this._restClientSource.assignPermissionLevel(this.context, selectedUser.subjectDescriptor!, definitionId, resourceId).then(
                async permissionLevelAssignment => {
                    resolve(permissionLevelAssignment);
                },
                reason => {
                    actionTelemetryLogger.captureError("Assign Permission Level Failed" + " : " + reason || reason.message);
                    resolve(null);
                }
            );
        });

        //TODO?:
        //Re-think if we need to make further updates to add.
        //Currently we are going with the logic of allowing direct assignments and for new members only.
    }

    public permissionLevelAssignmentsExistence = (selectedUser: IIdentity, projectId: string): Promise<boolean> => {
        return new Promise(async (resolve, reject) => {
            await this._restClientSource
                .getPermissionLevelAssignmentsBySubjectForProject(this.context, selectedUser.subjectDescriptor!, projectId)
                .then(
                    assignments => {
                        if (assignments.length > 0) {
                            return resolve(true);
                        } else {
                            return resolve(false);
                        }
                    },
                    reason => {
                        return resolve(false);
                    }
                );

            return resolve(false);
        });
    };

    public async getDefaultTeamDescriptor(projectId: string): Promise<string | undefined> {
        const project = await this._restClientSource.getTeamProject(this.context, projectId);
        if (project && project.defaultTeam) {
            const teamDescriptor = await this._restClientSource.getDescriptor(this.context, project.defaultTeam.id);
            if (teamDescriptor) {
                return teamDescriptor.value;
            }
        }
        return undefined;
    }

    public createEntity(identity: IIdentity, defaultTeamDescriptor: string | undefined): Promise<boolean> {
        return identity!.entityType.toLowerCase() === "user"
            ? this.createUser(identity!, defaultTeamDescriptor!)
            : this.createGroup(identity!, defaultTeamDescriptor!);
    }

    public createUser = (selectedUser: IIdentity, groupDescriptor: string): Promise<boolean> => {
        var actionTelemetryLogger = this._startActionCapture("Add member create user", {});

        return new Promise(async (resolve, reject) => {
            await this._restClientSource
                .createUser(this.context, { storageKey: "", originId: selectedUser.originId } as GraphUserOriginIdCreationContext, [groupDescriptor])
                .then(
                    async () => {
                        return resolve(true);
                    },
                    reason => {
                        actionTelemetryLogger.captureError(selectedUser.subjectDescriptor + " : " + reason || reason.message);
                        return resolve(false);
                    }
                );

            return resolve(false);
        });
    };

    public createGroup = (selectedGroup: IIdentity, groupDescriptor: string): Promise<boolean> => {
        var actionTelemetryLogger = this._startActionCapture("Add member create group", {});

        return new Promise(async (resolve, reject) => {
            await this._restClientSource
                .createGroup(this.context, { storageKey: "", originId: selectedGroup.originId } as GraphUserOriginIdCreationContext, "", [
                    groupDescriptor
                ])
                .then(
                    async () => {
                        return resolve(true);
                    },
                    reason => {
                        actionTelemetryLogger.captureError(selectedGroup.subjectDescriptor + " : " + reason || reason.message);
                        return resolve(false);
                    }
                );

            return resolve(false);
        });
    };
}
